import { Col, Row } from "react-bootstrap";
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";
import { teamGroupTeamsMappingsApi, teamsMasterDataApi } from "@/apis/apis";
import { useGet, usePut } from "@/apiHooks/HttpFunctions";

import AsyncDropdown from "@/components/AsyncDropdown/AsyncDropdown";
import BasicInput from "@/components/BasicInput";
import BasicModal from "@/Modals/BasicModal";
import { LIST_MODE } from "@/constant/Lists/listMode";
import RenderToast from "@/components/RenderToast";
import TeamGroupTeamInitialValues from "@/data/WebDataManagement/Mapping/TeamGroupTeam/initial-values";
import TeamGroupTeamSchema from "@/schema/WebDataManagement/Mapping/TeamGroupTeam/TeamGroupTeamSchema";
import axiosInstance from "@/Axios/ConfiguredAxios";
import { debounce } from "@mui/material";
import useCloseModalOnNotFound from "@/CustomHooks/useCloseModalOnNotFound";
import { useFormik } from "formik";
import useFormikErrorToast from "@/CustomHooks/useFormikErrorToast";
import useHttpErrorToast from "@/CustomHooks/useHttpErrorToast";
// import { DndContext, closestCenter, useSensors } from "@dnd-kit/core";
import { useSortable } from "@dnd-kit/sortable";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";

import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";

import { CSS } from "@dnd-kit/utilities";

const SortableItem = function (props) {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: props.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <tr ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <td className="Hidden px-3 py-3.5 text-sm text-gray-500 lg:table-cell">
        {props.id.value}
      </td>
      <td className="Hidden px-3 py-3.5 text-sm text-gray-500 lg:table-cell">
        {props.id.label}
      </td>
    </tr>
  );
};

const TeamGroupTeamModal = forwardRef((_, ref) => {
  const [defaultOptions, setDefaultOptions] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [items, setItems] = useState([]);

  const [saveError, setSaveError] = useState(null);
  const resetError = useCallback(() => {
    setSaveError(null);
  }, []);

  const [modalState, setModalState] = useState({ isOpen: false, id: null });

  const showApi = modalState.id
    ? `${teamGroupTeamsMappingsApi}/${modalState.id}`
    : null;

  const { data: showData, error: showError } = useGet({
    apiURL: showApi,
    enabled: !!modalState.id,
  });

  const listApi = `${teamsMasterDataApi}?mode=${LIST_MODE.SELECT}&filter[keyword]=`;

  useEffect(() => {
    if (!modalState.isOpen) return;

    const fetchDefaultOptions = async () => {
      setLoadingOptions(true);
      const response = await axiosInstance.get(listApi);

      if (response) {
        const options =
          response.data.data.map((val) => ({
            label: `${val.code} - ${val.name}${val.team_group_id ? "*" : ""}`,
            value: val.id,
          })) ?? [];

        const selectedTeams = showData?.data?.teams || [];

        selectedTeams.forEach((team) => {
          if (!options.some((opt) => opt.value === team.id)) {
            options.unshift({
              label: `${team.code} - ${team.name}${
                team.team_group_id ? "*" : ""
              }`,
              value: team.id,
            });
          }
        });

        setDefaultOptions(options);
      }

      setLoadingOptions(false);
    };

    fetchDefaultOptions();
  }, [listApi, modalState, showData]);

  const debouncedFetchOptions = debounce(async (input, callback) => {
    setLoadingOptions(true);
    const response = await axiosInstance.get(`${listApi}${input}`);

    if (response) {
      const newOptions =
        response.data.data
          .filter((item) => item.id !== modalState.id)
          .map((item) => ({
            label: `${item.code} - ${item.name}${
              item.team_group_id ? "*" : ""
            }`,
            value: item.id,
          })) ?? [];

      // Append new options to defaultOptions if not already present
      setDefaultOptions((prev) => {
        const existingIds = prev.map((opt) => opt.value);
        return [
          ...prev,
          ...newOptions.filter((option) => !existingIds.includes(option.value)),
        ];
      });

      callback(newOptions);
    }

    setLoadingOptions(false);
  }, 500);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const getSelectedOption = () => {
    if (!formik.values.team_ids?.length) return [];
    return formik.values.team_ids.map((id) => {
      // Attempt to find the option in the defaultOptions
      const foundOption = defaultOptions.find((opt) => opt.value === id);
      if (foundOption) {
        return foundOption;
      }

      // Fallback: Dynamically create the option
      const matchingTeam = showData?.data?.teams.find((v) => v.id === id);
      return {
        label: matchingTeam
          ? `${matchingTeam.code} - ${matchingTeam.name}${
              matchingTeam.team_group_id ? "*" : ""
            }`
          : `Unknown (${id})`,
        value: id,
      };
    });
  };

  useEffect(() => {
    const selectedOptions = getSelectedOption();
    setItems(selectedOptions);
  }, [defaultOptions]);

  const handleSuccess = (action) => (data) => {
    closeModal();
    RenderToast({
      type: "success",
      message: `Team Group - Team ${action} successfully.`,
    });
  };

  const {
    mutate: saveEdit,
    isPending: saveIsPending,
    error: saveEditError,
  } = usePut({
    apiURL: `${teamGroupTeamsMappingsApi}/${modalState.id}`,
    onSuccess: handleSuccess("edited"),
  });

  useEffect(() => {
    setSaveError(saveEditError);
  }, [saveEditError]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: TeamGroupTeamInitialValues(modalState.id ? showData : null),
    validationSchema: TeamGroupTeamSchema(),
    onSubmit: (values) => handleSubmit(values),
  });

  const handleSubmit = (values) => {
    const payload = {
      team_ids: values.team_ids,
    };
    saveEdit(JSON.stringify(payload));
  };

  useImperativeHandle(ref, () => ({
    open: (props) => setModalState({ isOpen: true, id: props?.id }),
    close: closeModal,
  }));

  const closeModal = useCallback(() => {
    formik.resetForm();
    resetError();
    setModalState({ isOpen: false, id: null });
  }, [resetError, formik]);

  useCloseModalOnNotFound(showError, closeModal);
  useFormikErrorToast(formik);
  useHttpErrorToast(saveError);

  const {
    mutate: updateSort,
    isPending: updateSortPending,
    error: updateSortError,
  } = usePut({
    apiURL: `${teamsMasterDataApi}/sort`,
    isFormData: false,
    onSuccess: (response) => {
      handleSuccess(response.message);
    },
  });

  function handleDragEnd(event) {
    const { active, over } = event;

    if (active && over && active.id !== over.id) {
      const oldIndex = items.indexOf(active.id);
      const newIndex = items.indexOf(over.id);

      // Handle call sort api
      updateSort(
        JSON.stringify({
          id: active.id.value,
          old_position: oldIndex + 1,
          new_position: newIndex + 1,
        })
      );

      setItems((data) => {
        const updatedData = arrayMove(data, oldIndex, newIndex);
        return updatedData;
      });
    }
  }

  if (!showData) return null;

  return (
    <BasicModal
      header={
        modalState.id ? "Edit Team Group - Team" : "Add Team Group - Team"
      }
      onHide={closeModal}
      show={modalState.isOpen}
      saveLoading={saveIsPending}
      formik={formik}
    >
      <Row>
        <Col md={12}>
          <BasicInput
            formik={formik}
            saveError={saveError}
            label="Team Group"
            disabled={true}
            value={`${showData.data.code} - ${showData.data.name}`}
          />
        </Col>
      </Row>
      <Row>
        <Col md={12}>
          <AsyncDropdown
            hintText="Asterisk symbol (*) means it is already mapped to another Team Group.
                  Selecting it will replace the Team Group it is already associated with."
            isMulti={true}
            label="Teams"
            placeholder="No team selected. Type to search..."
            defaultOptions={defaultOptions}
            loadOptions={debouncedFetchOptions}
            isLoading={loadingOptions}
            value={getSelectedOption()}
            onChange={(selected) => {
              if (selected) {
                // Add selected option to defaultOptions if not already present
                setDefaultOptions((prev) => {
                  if (!prev.some((opt) => opt.value === selected.value)) {
                    return [
                      ...prev,
                      { label: selected.label, value: selected.value },
                    ];
                  }
                  return prev;
                });

                // Update formik value
                formik.setFieldValue(
                  "team_ids",
                  selected.map((sel) => sel.value)
                );
              } else {
                formik.setFieldValue("team_ids", []);
              }
            }}
          />
        </Col>
      </Row>
      <Row>
        <Col md={12}>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={items}
              strategy={verticalListSortingStrategy}
            >
              <table className="min-w-full divide-y divide-gray-300 border">
                <thead>
                  <tr>
                    <th className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                      Order
                    </th>
                    <th className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                      Team
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => (
                    <SortableItem key={item.code} id={item} />
                  ))}
                </tbody>
              </table>
            </SortableContext>
          </DndContext>
        </Col>
      </Row>
    </BasicModal>
  );
});

export default TeamGroupTeamModal;
